import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { calcDays, priceBreakdown, usd } from "@/lib/pricing";
import type { DateRange } from "react-day-picker";
import { createBookingCheckout } from "@/lib/payments.functions";
import { track } from "@/lib/analytics";
import { createLicenseUploadUrls } from "@/lib/licenses.functions";
import { StripeEmbeddedCheckoutPanel } from "@/components/StripeEmbeddedCheckoutPanel";
import { getStripeEnvironment } from "@/lib/stripe";

const bookingPageQuery = (id: string) => queryOptions({
  queryKey: ["booking-page", id],
  queryFn: async () => {
    const [carRes, settingsRes, rangesRes] = await Promise.all([
      supabase.from("cars").select("*").eq("id", id).eq("active", true).maybeSingle(),
      supabase.from("settings").select("*").eq("id", 1).maybeSingle(),
      supabase.rpc("get_unavailable_ranges"),
    ]);
    if (carRes.error) throw carRes.error;
    if (!carRes.data) throw notFound();
    const ranges = (rangesRes.data ?? []).filter((r: any) => r.car_id === id) as { start_date: string; end_date: string }[];
    return { car: carRes.data, settings: settingsRes.data ?? { tax_rate: 0, cleaning_fee: 0 }, ranges };
  },
});

const searchSchema = z.object({
  start: fallback(z.string().optional(), undefined),
  end: fallback(z.string().optional(), undefined),
});

export const Route = createFileRoute("/booking/$carId")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({ meta: [
    { title: "Request Booking — Creed Car Rental Miami" },
    { name: "description", content: "Request your luxury rental from Creed Car Rental. We reply within a few hours." },
  ] }),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(bookingPageQuery(params.carId)),
  component: Booking,
  errorComponent: () => <div className="p-10 text-center">Failed to load.</div>,
  notFoundComponent: () => <div className="p-10 text-center">Vehicle not found.</div>,
});

const PICKUP_OPTIONS = [
  "MIA — Miami International Airport",
  "FLL — Fort Lauderdale Airport",
  "Hotel (South Beach)",
  "Hotel (Brickell / Downtown)",
  "Airbnb / Residence",
  "Other (specify in notes)",
];

const MAX_LICENSE_BYTES = 5 * 1024 * 1024; // 5MB

const schema = z.object({
  renter_name: z.string().trim().min(2).max(100),
  renter_email: z.string().trim().email().max(255),
  renter_phone: z.string().trim().min(7).max(30),
  renter_dob: z.string().min(1, "Date of birth is required"),
  license_number: z.string().trim().min(3).max(50),
  start_date: z.string().min(1),
  end_date: z.string().min(1),
  pickup_location: z.string().min(1, "Choose a pickup location"),
  pickup_address: z.string().trim().max(200).optional(),
  notes: z.string().max(500).optional(),
});

function toISO(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fromISO(s: string) { return new Date(s + "T00:00:00"); }
function fmtNice(s: string) {
  return fromISO(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function Booking() {
  const { carId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data: { car, settings, ranges } } = useSuspenseQuery(bookingPageQuery(carId));
  const checkoutFn = useServerFn(createBookingCheckout);
  const createUploadUrlsFn = useServerFn(createLicenseUploadUrls);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [startDate, setStartDate] = useState(search.start ?? "");
  const [endDate, setEndDate] = useState(search.end ?? "");
  const [pickup, setPickup] = useState("");
  const [licenseFront, setLicenseFront] = useState<File | null>(null);
  const [licenseBack, setLicenseBack] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const days = useMemo(() => calcDays(startDate, endDate), [startDate, endDate]);
  const breakdown = useMemo(
    () => priceBreakdown(car as any, settings as any, days || 1),
    [car, settings, days]
  );

  const conflict = useMemo(() => {
    if (!startDate || !endDate || startDate > endDate) return false;
    return ranges.some(r => startDate <= r.end_date && r.start_date <= endDate);
  }, [ranges, startDate, endDate]);

  const disabledDays = useMemo(
    () => ranges.map(r => ({ from: fromISO(r.start_date), to: fromISO(r.end_date) })),
    [ranges]
  );
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const range: DateRange | undefined =
    startDate ? { from: fromISO(startDate), to: endDate ? fromISO(endDate) : undefined } : undefined;

  const onRangeSelect = (r: DateRange | undefined) => {
    setStartDate(r?.from ? toISO(r.from) : "");
    setEndDate(r?.to ? toISO(r.to) : "");
  };

  const goToStep2 = () => {
    if (!startDate || !endDate) { toast.error("Please pick your dates"); return; }
    if (calcDays(startDate, endDate) < 1) { toast.error("Return must be after pickup"); return; }
    if (conflict) { toast.error("This vehicle is unavailable for those dates"); return; }
    if (!pickup) { toast.error("Choose a pickup location"); return; }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const validateLicenseFile = (file: File, label: string): string | null => {
    if (!file.type.startsWith("image/")) return `${label} must be an image (JPG/PNG/HEIC).`;
    if (file.size > MAX_LICENSE_BYTES) return `${label} is too large — max 5MB.`;
    return null;
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const raw = Object.fromEntries(form);
    const parsed = schema.safeParse({ ...raw, start_date: startDate, end_date: endDate, pickup_location: pickup });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || "Please check your inputs");
      return;
    }
    if (!licenseFront || !licenseBack) {
      toast.error("Please upload both sides of your driver's license");
      return;
    }
    const frontErr = validateLicenseFile(licenseFront, "License front");
    if (frontErr) { toast.error(frontErr); return; }
    const backErr = validateLicenseFile(licenseBack, "License back");
    if (backErr) { toast.error(backErr); return; }

    setSubmitting(true);
    try {
      const urls = await createUploadUrlsFn({
        data: { frontName: licenseFront.name, backName: licenseBack.name },
      });
      if ("error" in urls) throw new Error(urls.error);

      const uploadWithToken = async (file: File, target: { path: string; token: string }, label: string) => {
        const { error } = await supabase.storage
          .from("licenses")
          .uploadToSignedUrl(target.path, target.token, file);
        if (error) throw new Error(`License ${label} upload failed: ${error.message}`);
        return target.path;
      };
      const [frontPath, backPath] = await Promise.all([
        uploadWithToken(licenseFront, urls.front, "front"),
        uploadWithToken(licenseBack, urls.back, "back"),
      ]);

      const result = await checkoutFn({
        data: {
          car_id: car.id,
          renter_name: parsed.data.renter_name,
          renter_email: parsed.data.renter_email,
          renter_phone: parsed.data.renter_phone,
          renter_dob: parsed.data.renter_dob,
          license_number: parsed.data.license_number,
          license_front_url: frontPath,
          license_back_url: backPath,
          start_date: parsed.data.start_date,
          end_date: parsed.data.end_date,
          pickup_location: parsed.data.pickup_location,
          pickup_address: parsed.data.pickup_address,
          notes: parsed.data.notes,
          return_url: `${window.location.origin}/booking/success`,
          environment: getStripeEnvironment(),
        },
      });
      if ("error" in result) {
        toast.error(result.error);
        return;
      }
      setClientSecret(result.clientSecret);
      track("begin_checkout", {
        currency: "USD",
        value: breakdown.total,
        items: [{
          item_id: (car as any).id,
          item_name: `${(car as any).year} ${(car as any).make} ${(car as any).model}`,
          item_brand: (car as any).make,
          price: Number((car as any).daily_rate) || 0,
          quantity: days || 1,
        }],
      });
      track("booking_created", {
        booking_id: (result as any).booking_id,
        car_id: (car as any).id,
        days: days || 1,
        value: breakdown.total,
        currency: "USD",
      });
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to submit booking request");
    } finally {
      setSubmitting(false);
    }
  };

  const continueDisabled = days < 1 || !pickup || conflict;
  const continueHint = !startDate || !endDate
    ? "Pick both pickup and return dates to continue."
    : days < 1 ? "Return date must be after pickup."
    : !pickup ? "Choose a pickup location."
    : conflict ? "Those dates overlap an existing booking."
    : "";

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />

      {/* Hero header */}
      <section className="relative isolate overflow-hidden border-b border-border/60 bg-espresso py-16 md:py-20">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 80% at 85% 20%, oklch(0.72 0.15 65 / 0.35), transparent 60%), radial-gradient(50% 70% at 10% 90%, oklch(0.72 0.15 65 / 0.18), transparent 60%)",
          }}
          aria-hidden
        />
        <svg
          className="pointer-events-none absolute -right-24 -top-24 h-[520px] w-[520px] text-accent/20"
          viewBox="0 0 600 600"
          fill="none"
          aria-hidden
        >
          <circle cx="300" cy="300" r="220" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="160" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="100" stroke="currentColor" strokeWidth="1" />
        </svg>
        <div className="relative mx-auto max-w-6xl px-6">
          <Link to="/cars/$carId" params={{ carId: car.id }} className="text-sm text-cream/60 hover:text-accent">← Back to vehicle</Link>
          <h1 className="mt-4 font-display text-4xl text-cream md:text-5xl">Request your booking</h1>
          <p className="text-cream/70">{car.year} {car.make} {car.model}</p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-10">
        <div className="mt-3 text-xs text-muted-foreground">
          Renters must be 25+ with a valid driver's license. We reply within a few hours to confirm availability and arrange pickup.
        </div>

        {/* Step indicator */}
        <div className="mt-6 flex items-center gap-3 text-sm flex-wrap">
          <span className={`rounded-full px-3 py-1 ${step === 1 ? "bg-accent text-espresso" : "bg-secondary text-espresso/70"}`}>1. Dates</span>
          <span className="text-muted-foreground">→</span>
          <span className={`rounded-full px-3 py-1 ${step === 2 ? "bg-accent text-espresso" : "bg-secondary text-espresso/70"}`}>2. Driver info</span>
          <span className="text-muted-foreground">→</span>
          <span className={`rounded-full px-3 py-1 ${step === 3 ? "bg-accent text-espresso" : "bg-secondary text-espresso/70"}`}>3. Payment</span>
        </div>

        <div className="mt-6 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6 rounded-lg border border-border bg-card p-6">
            {step === 1 && (
              <>
                <Field label="Pickup & return dates">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate && endDate
                          ? `${fmtNice(startDate)} → ${fmtNice(endDate)}`
                          : startDate
                            ? `${fmtNice(startDate)} → pick return`
                            : "Select dates"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="range"
                        selected={range}
                        onSelect={onRangeSelect}
                        disabled={[{ before: today }, ...disabledDays]}
                        numberOfMonths={1}
                        initialFocus
                        className="pointer-events-auto p-3"
                      />
                    </PopoverContent>
                  </Popover>
                </Field>
                {ranges.length > 0 && (
                  <div className="rounded-md border border-border bg-secondary/40 p-3 text-xs text-espresso/80">
                    <div className="font-medium text-espresso">Already reserved</div>
                    <ul className="mt-1 space-y-0.5">
                      {ranges
                        .slice()
                        .sort((a, b) => a.start_date.localeCompare(b.start_date))
                        .map((r, i) => (
                          <li key={i}>• {fmtNice(r.start_date)} — {fmtNice(r.end_date)}</li>
                        ))}
                    </ul>
                  </div>
                )}
                {conflict && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                    This vehicle is already booked for part of those dates. Please pick a different range.
                  </div>
                )}
                <Field label="Pickup location">
                  <select
                    value={pickup}
                    onChange={e => setPickup(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Select a pickup option…</option>
                    {PICKUP_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <div className="space-y-2">
                  <Button type="button" size="lg" onClick={goToStep2} className="w-full bg-primary hover:bg-primary/90" disabled={continueDisabled}>
                    Continue to driver info
                  </Button>
                  {continueDisabled && continueHint && (
                    <p className="text-center text-xs text-muted-foreground">{continueHint}</p>
                  )}
                </div>
              </>
            )}

            {step === 2 && (
              <form onSubmit={onSubmit} className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Full name"><Input name="renter_name" required maxLength={100} /></Field>
                  <Field label="Email"><Input type="email" name="renter_email" required maxLength={255} /></Field>
                  <Field label="Phone"><Input name="renter_phone" required maxLength={30} /></Field>
                  <Field label="Date of birth (25+)"><Input type="date" name="renter_dob" required /></Field>
                  <Field label="Driver's license #"><Input name="license_number" required maxLength={50} /></Field>
                  <Field label="Pickup address (if applicable)"><Input name="pickup_address" maxLength={200} placeholder="Hotel name or street address" /></Field>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="License — front photo">
                    <Input type="file" accept="image/*" onChange={e => setLicenseFront(e.target.files?.[0] || null)} required />
                  </Field>
                  <Field label="License — back photo">
                    <Input type="file" accept="image/*" onChange={e => setLicenseBack(e.target.files?.[0] || null)} required />
                  </Field>
                </div>
                <p className="-mt-3 text-xs text-muted-foreground">JPG, PNG or HEIC, up to 5MB each.</p>
                <Field label="Notes (optional)"><Textarea name="notes" maxLength={500} placeholder="Special requests, arrival time, additional drivers…" /></Field>

                <div className="rounded border border-border bg-secondary/40 p-3 text-xs text-muted-foreground">
                  You'll be charged the full rental total plus refundable security deposit now. The owner reviews license and details within a few hours — if declined for any reason, you'll be refunded in full.
                </div>

                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">← Back</Button>
                  <Button type="submit" size="lg" className="flex-[2] bg-primary hover:bg-primary/90" disabled={submitting}>
                    {submitting ? "Preparing checkout…" : `Continue to payment · ${usd(breakdown.total + (Number((car as any).security_deposit) || 0))}`}
                  </Button>
                </div>
              </form>
            )}

            {step === 3 && clientSecret && (
              <div className="space-y-4">
                <div className="rounded border border-border bg-secondary/40 p-3 text-xs text-muted-foreground">
                  Complete your payment below. The slot is held for 30 minutes — close this page and you'll need to start over.
                </div>
                <StripeEmbeddedCheckoutPanel clientSecret={clientSecret} />
              </div>
            )}
          </div>

          <aside className="h-fit rounded-lg border border-border bg-card p-6">
            <div className="font-display text-xl text-espresso">Price estimate</div>
            <div className="mt-4 space-y-2 text-sm">
              <PriceRow label={`${usd(breakdown.daily)} × ${breakdown.days} day${breakdown.days===1?"":"s"}`} value={usd(breakdown.subtotal)} />
              {breakdown.discount > 0 && <PriceRow label="Multi-day discount" value={`−${usd(breakdown.discount)}`} accent />}
              {breakdown.cleaning_fee > 0 && <PriceRow label="Cleaning fee" value={usd(breakdown.cleaning_fee)} />}
              {breakdown.tax > 0 && <PriceRow label="Tax" value={usd(breakdown.tax)} />}
              <div className="my-3 h-px bg-border" />
              <PriceRow label="Total" value={usd(breakdown.total)} bold />
              {Number(car.security_deposit) > 0 && (
                <PriceRow label="Refundable deposit" value={usd(Number(car.security_deposit))} muted />
              )}
            </div>
            <div className="mt-4 text-xs text-muted-foreground">Insurance included. Tolls, tickets, cleaning &amp; late fees not included. Final quote confirmed by owner.</div>
          </aside>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1.5"><Label className="text-espresso">{label}</Label>{children}</div>;
}
function PriceRow({ label, value, bold, muted, accent }: { label: string; value: string; bold?: boolean; muted?: boolean; accent?: boolean }) {
  return (
    <div className={`flex justify-between gap-3 ${bold ? "font-display text-lg text-espresso" : ""} ${muted ? "text-muted-foreground text-xs" : ""} ${accent ? "text-accent" : ""}`}>
      <span>{label}</span><span className="whitespace-nowrap">{value}</span>
    </div>
  );
}
